/**
 * Subscription Dashboard Page
 * Displays subscription details, usage stats, billing history, and payment management
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionService } from '../services/subscription.service';
import { serviceProviderService, type Subscription } from '../services/service-provider.service';
import type { StripeAccountStatus, Invoice } from '../types';
import { toast } from '@/lib/utils/toast';

const formatCurrency = (cents: number): string => {
  return `$${(cents / 100).toFixed(2)}`;
};

const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getTierDisplayName = (tier: string): string => {
  const tierNames: Record<string, string> = {
    free: 'Free',
    basic: 'Basic',
    pro: 'Pro',
    enterprise: 'Enterprise',
  };
  return tierNames[tier] || tier;
};

const getTierPrice = (tier: string): number => {
  const prices: Record<string, number> = {
    free: 0,
    basic: 99,
    pro: 249,
    enterprise: 499,
  };
  return prices[tier] || 0;
};

export const SubscriptionDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  useEffect(() => {
    // Check authentication
    if (!serviceProviderService.isAuthenticated()) {
      navigate('/provider/login');
      return;
    }

    loadDashboardData();
  }, [navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load subscription data
      const subscriptionData = await subscriptionService.getSubscription();
      setSubscription(subscriptionData);

      // Load Stripe Connect status
      try {
        const status = await subscriptionService.getStripeStatus();
        setStripeStatus(status);
      } catch (err) {
        console.warn('Failed to load Stripe status:', err);
      }

      // Load invoices (if available)
      try {
        const invoiceData = await subscriptionService.getInvoices();
        setInvoices(invoiceData.invoices);
      } catch (err) {
        console.warn('Invoice history not available:', err);
      }
    } catch (err) {
      console.error('Failed to load subscription data:', err);
      setError('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    const confirmed = window.confirm(
      'Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.'
    );

    if (!confirmed) return;

    try {
      setCancellingSubscription(true);
      const response = await subscriptionService.cancelSubscription();

      toast.success(
        `Subscription will be cancelled on ${formatDate(response.cancels_at)}. You will retain access until then.`
      );

      // Reload subscription data
      await loadDashboardData();
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      toast.error('Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleCompleteStripeOnboarding = async () => {
    try {
      const response = await subscriptionService.getStripeOnboardingLink();
      window.location.href = response.onboardingUrl;
    } catch (err) {
      console.error('Failed to get onboarding link:', err);
      toast.error('Failed to start onboarding process. Please try again.');
    }
  };

  const calculateUsagePercentage = (): number => {
    if (!subscription || !subscription.monthly_job_limit) {
      return 0;
    }
    // This would come from actual job count API in production
    // For now, placeholder logic
    return 0;
  };

  if (!serviceProviderService.isAuthenticated()) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="card text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-neutral-600">Loading subscription details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="min-h-screen bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-900">{error || 'Failed to load subscription'}</p>
          </div>
        </div>
      </div>
    );
  }

  const usagePercentage = calculateUsagePercentage();
  const isFreeTier = subscription.tier === 'free';
  const isPaidTier = !isFreeTier;
  const needsStripeOnboarding = isPaidTier && stripeStatus && !stripeStatus.charges_enabled;

  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">
            Subscription Dashboard
          </h1>
          <p className="text-neutral-600">
            Manage your subscription and billing information
          </p>
        </div>

        {/* Stripe Onboarding Alert */}
        {needsStripeOnboarding && (
          <div className="card bg-yellow-50 border-yellow-200 mb-6">
            <div className="flex items-start">
              <svg
                className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div className="flex-grow">
                <h3 className="font-semibold text-yellow-900 mb-2">
                  Complete Payment Setup
                </h3>
                <p className="text-yellow-800 mb-4">
                  You need to complete Stripe Connect onboarding to receive deposit payments from customers.
                </p>
                <button
                  onClick={handleCompleteStripeOnboarding}
                  className="bg-yellow-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-yellow-700 transition-colors"
                >
                  Complete Setup
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Current Plan */}
          <div className="card lg:col-span-2">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">
              Current Plan
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm text-neutral-600">Plan Tier</dt>
                    <dd className="text-lg font-semibold text-neutral-900">
                      {getTierDisplayName(subscription.tier)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-neutral-600">Monthly Price</dt>
                    <dd className="text-lg font-semibold text-neutral-900">
                      ${getTierPrice(subscription.tier)}/month
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-neutral-600">Status</dt>
                    <dd>
                      <span
                        className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                          subscription.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : subscription.status === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
              <div>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm text-neutral-600">Billing Cycle</dt>
                    <dd className="text-sm text-neutral-900">
                      {formatDate(subscription.current_period_start)} -{' '}
                      {formatDate(subscription.current_period_end)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-neutral-600">Auto-Renewal</dt>
                    <dd className="text-sm text-neutral-900">
                      {subscription.cancel_at_period_end ? 'Disabled' : 'Enabled'}
                    </dd>
                  </div>
                  {subscription.cancel_at_period_end === 1 && (
                    <div>
                      <dt className="text-sm text-neutral-600">Cancels On</dt>
                      <dd className="text-sm text-red-800 font-semibold">
                        {formatDate(subscription.current_period_end)}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>

          {/* Usage Stats */}
          <div className="card">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">
              Usage This Month
            </h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-sm text-neutral-600">Jobs</span>
                  <span className="text-sm font-semibold text-neutral-900">
                    {subscription.monthly_job_limit
                      ? `0 / ${subscription.monthly_job_limit}`
                      : 'Unlimited'}
                  </span>
                </div>
                {subscription.monthly_job_limit && (
                  <div className="w-full bg-neutral-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all"
                      style={{ width: `${usagePercentage}%` }}
                    />
                  </div>
                )}
              </div>
              <div>
                <dt className="text-sm text-neutral-600 mb-1">Transaction Fee</dt>
                <dd className="text-2xl font-bold text-neutral-900">
                  {subscription.transaction_fee_percent}%
                </dd>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="card mb-6">
          <h2 className="text-xl font-bold text-neutral-900 mb-4">Actions</h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => navigate('/provider/subscription/plans')}
              className="bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
            >
              {isFreeTier ? 'Upgrade Plan' : 'Change Plan'}
            </button>
            {isPaidTier && subscription.status === 'active' && !subscription.cancel_at_period_end && (
              <button
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
                className="bg-white text-red-600 border-2 border-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                {cancellingSubscription ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            )}
          </div>
        </div>

        {/* Billing History */}
        <div className="card">
          <h2 className="text-xl font-bold text-neutral-900 mb-4">
            Billing History
          </h2>
          {invoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                      Invoice
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="px-4 py-3 text-sm text-neutral-900">
                        {formatDate(invoice.created)}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-neutral-900">
                        {formatCurrency(invoice.amount_paid)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                            invoice.status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : invoice.status === 'open'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <a
                          href={invoice.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-800 font-medium"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-neutral-600">
                {isFreeTier
                  ? 'No billing history available for free tier'
                  : 'No invoices yet'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
